import { supabase } from "../../../../lib/supabase";
import { useState, useRef } from "react";
import ImageUploader from "@/components/base/ImageUploader";
import RichTextEditor from "@/components/base/RichTextEditor";
import {
  BuilderBlock,
  HeroBlockData,
  StoryBlockData,
  StoryItem,
  ApplicationsBlockData,
  ApplicationItem,
  CertificationsBlockData,
  CertificationItem,
  CTABlockData,
  SpecsGridBlockData,
  GalleryBlockData,
  VideoBlockData,
} from "./types";

interface Props {
  block: BuilderBlock;
  lang: "vi" | "kr";
  onChange: (updated: BuilderBlock) => void;
}

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

// ── Hero Editor ───────────────────────────────────────────────────────────────
const HeroEditor: React.FC<{ data: HeroBlockData; onChange: (d: HeroBlockData) => void }> = ({ data, onChange }) => {
  const set = <K extends keyof HeroBlockData>(k: K, v: HeroBlockData[K]) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
        <i className="ri-image-2-line" /> Hero Banner
      </p>
      <div>
        <label className={labelCls}>Ảnh nền</label>
        <ImageUploader
          images={data.backgroundImage ? [data.backgroundImage] : []}
          onChange={(urls) => set("backgroundImage", urls[0] ?? "")}
          maxImages={1}
          folder="products"
          label="Ảnh nền hero (1920x800px)"
        />
      </div>
      <div>
        <label className={labelCls}>Độ tối overlay ({data.overlayOpacity}%)</label>
        <input
          type="range" min={0} max={90} step={5}
          value={data.overlayOpacity}
          onChange={(e) => set("overlayOpacity", Number(e.target.value))}
          className="w-full accent-[#004A99]"
        />
      </div>
      <div>
        <label className={labelCls}>Tagline (chữ nhỏ phía trên)</label>
        <input className={inputCls} value={data.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="CÔNG NGHỆ HÀN QUỐC" />
      </div>
      <div>
        <label className={labelCls}>Tiêu đề chính *</label>
        <input className={inputCls} value={data.title} onChange={(e) => set("title", e.target.value)} placeholder="Tên sản phẩm nổi bật" />
      </div>
      <div>
        <label className={labelCls}>Mô tả phụ</label>
        <textarea className={`${inputCls} resize-none`} rows={2} value={data.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Nút CTA - Nhãn</label>
          <input className={inputCls} value={data.ctaLabel} onChange={(e) => set("ctaLabel", e.target.value)} placeholder="Liên hệ báo giá" />
        </div>
        <div>
          <label className={labelCls}>Căn chỉnh chữ</label>
          <select className={inputCls} value={data.textAlign} onChange={(e) => set("textAlign", e.target.value as HeroBlockData["textAlign"])}>
            <option value="left">Trái</option>
            <option value="center">Giữa</option>
            <option value="right">Phải</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// ── Story Editor ──────────────────────────────────────────────────────────────
const StoryEditor: React.FC<{ data: StoryBlockData; onChange: (d: StoryBlockData) => void }> = ({ data, onChange }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(0);

  const addItem = () => {
    const newItem: StoryItem = {
      id: `story-${Date.now()}`,
      title: "",
      content: "",
      image: "",
      imagePosition: data.items.length % 2 === 0 ? "left" : "right",
      accent: "",
    };
    onChange({ ...data, items: [...data.items, newItem] });
    setExpandedIdx(data.items.length);
  };

  const updateItem = (idx: number, updated: Partial<StoryItem>) => {
    const items = data.items.map((item, i) => i === idx ? { ...item, ...updated } : item);
    onChange({ ...data, items });
  };

  const removeItem = (idx: number) => {
    onChange({ ...data, items: data.items.filter((_, i) => i !== idx) });
    setExpandedIdx(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <i className="ri-layout-column-line" /> Product Story ({data.items.length} blocks)
        </p>
        <button type="button" onClick={addItem} className="flex items-center gap-1 px-3 py-1.5 bg-[#004A99] text-white text-xs rounded-lg cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Thêm block
        </button>
      </div>

      {data.items.length === 0 && (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
          Bấm &quot;Thêm block&quot; để tạo story section
        </div>
      )}

      {data.items.map((item, idx) => (
        <div key={item.id} className="border border-gray-200 rounded-xl overflow-hidden">
          <div
            className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100"
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
          >
            <span className={idx % 2 === 0 ? "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 bg-[#004A99] text-white" : "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 bg-[#00AEEF] text-white"}>{idx + 1}</span>
            <span className="flex-1 text-xs font-semibold text-gray-700 truncate">{item.title || `Story block ${idx + 1}`}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); removeItem(idx); }} className="text-red-400 hover:text-red-600 cursor-pointer text-xs px-1">
              <i className="ri-delete-bin-line" />
            </button>
            {expandedIdx === idx ? <i className="ri-arrow-up-s-line text-gray-400 text-sm" /> : <i className="ri-arrow-down-s-line text-gray-400 text-sm" />}
          </div>
          {expandedIdx === idx && (
            <div className="p-4 space-y-3 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tiêu đề *</label>
                  <input className={inputCls} value={item.title} onChange={(e) => updateItem(idx, { title: e.target.value })} placeholder="Công nghệ tiên tiến" />
                </div>
                <div>
                  <label className={labelCls}>Accent label</label>
                  <input className={inputCls} value={item.accent} onChange={(e) => updateItem(idx, { accent: e.target.value })} placeholder="CÔNG NGHỆ MỚI" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Vị trí ảnh</label>
                <div className="flex gap-2">
                  {(["left", "right"] as const).map((pos) => (
                    <button key={pos} type="button" onClick={() => updateItem(idx, { imagePosition: pos })}
                      className={item.imagePosition === pos ? "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap bg-[#004A99] text-white border-[#004A99]" : "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap bg-white text-gray-600 border-gray-200"}>
                      <i className={pos === "left" ? "ri-layout-left-2-line" : "ri-layout-right-2-line"} />
                      {pos === "left" ? "Ảnh trái" : "Ảnh phải"}
                    </button>
                  ))}
                </div>
              </div>
              <RichTextEditor
                value={item.content}
                onChange={(html) => updateItem(idx, { content: html })}
                placeholder="Nội dung mô tả..."
                minHeight={140}
                folder="products"
                label="Nội dung"
              />
              <ImageUploader
                images={item.image ? [item.image] : []}
                onChange={(urls) => updateItem(idx, { image: urls[0] ?? "" })}
                maxImages={1}
                folder="products"
                label="Hình ảnh minh họa"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Applications Editor ───────────────────────────────────────────────────────
const ApplicationsEditor: React.FC<{ data: ApplicationsBlockData; onChange: (d: ApplicationsBlockData) => void }> = ({ data, onChange }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const addItem = () => {
    const newItem: ApplicationItem = { id: `app-${Date.now()}`, title: "", description: "", image_url: "" };
    onChange({ ...data, items: [...data.items, newItem] });
    setExpandedIdx(data.items.length);
  };

  const updateItem = (idx: number, updated: Partial<ApplicationItem>) => {
    onChange({ ...data, items: data.items.map((item, i) => i === idx ? { ...item, ...updated } : item) });
  };

  const removeItem = (idx: number) => {
    onChange({ ...data, items: data.items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        <i className="ri-apps-line" /> Ứng dụng thực tế
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Tiêu đề section</label>
          <input className={inputCls} value={data.title} onChange={(e) => onChange({ ...data, title: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Layout</label>
          <select className={inputCls} value={data.layout} onChange={(e) => onChange({ ...data, layout: e.target.value as ApplicationsBlockData["layout"] })}>
            <option value="grid">Grid đều</option>
            <option value="masonry">Masonry</option>
            <option value="carousel">Carousel</option>
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>Mô tả phụ</label>
        <input className={inputCls} value={data.subtitle} onChange={(e) => onChange({ ...data, subtitle: e.target.value })} />
      </div>

      <div className="space-y-2">
        {data.items.map((item, idx) => (
          <div key={item.id} className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}>
              <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{idx + 1}</span>
              <span className="flex-1 text-xs font-semibold text-gray-700 truncate">{item.title || `Ứng dụng ${idx + 1}`}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); removeItem(idx); }} className="text-red-400 hover:text-red-600 cursor-pointer text-xs px-1"><i className="ri-delete-bin-line" /></button>
              {expandedIdx === idx ? <i className="ri-arrow-up-s-line text-gray-400 text-sm" /> : <i className="ri-arrow-down-s-line text-gray-400 text-sm" />}
            </div>
            {expandedIdx === idx && (
              <div className="p-4 space-y-3 border-t border-gray-100">
                <div>
                  <label className={labelCls}>Tiêu đề *</label>
                  <input className={inputCls} value={item.title} onChange={(e) => updateItem(idx, { title: e.target.value })} placeholder="VD: Bao bì mềm" />
                </div>
                <RichTextEditor value={item.description} onChange={(html) => updateItem(idx, { description: html })} placeholder="Mô tả ứng dụng..." minHeight={100} folder="applications" label="Mô tả" />
                <ImageUploader images={item.image_url ? [item.image_url] : []} onChange={(urls) => updateItem(idx, { image_url: urls[0] ?? "" })} maxImages={1} folder="applications" label="Hình ảnh nền (1920x800px)" />
              </div>
            )}
          </div>
        ))}
      </div>

      <button type="button" onClick={addItem} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-[#004A99] hover:text-[#004A99] transition-colors cursor-pointer">
        <i className="ri-add-line" /> Thêm ứng dụng mới
      </button>
    </div>
  );
};

// ── Certifications Editor ─────────────────────────────────────────────────────
const CertificationsEditor: React.FC<{ data: CertificationsBlockData; onChange: (d: CertificationsBlockData) => void }> = ({ data, onChange }) => {
  const addItem = () => {
    const newItem: CertificationItem = { id: `cert-${Date.now()}`, title: "", type: "certification", description: "", image_url: "" };
    onChange({ ...data, items: [...data.items, newItem] });
  };

  const updateItem = (idx: number, updated: Partial<CertificationItem>) => {
    onChange({ ...data, items: data.items.map((item, i) => i === idx ? { ...item, ...updated } : item) });
  };

  const removeItem = (idx: number) => {
    onChange({ ...data, items: data.items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        <i className="ri-award-line" /> Bằng sáng chế &amp; Chứng nhận
      </p>
      <div>
        <label className={labelCls}>Tiêu đề section</label>
        <input className={inputCls} value={data.title} onChange={(e) => onChange({ ...data, title: e.target.value })} />
      </div>
      <div className="space-y-3">
        {data.items.map((item, idx) => (
          <div key={item.id} className="border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-700">Mục {idx + 1}</span>
              <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 cursor-pointer text-xs"><i className="ri-delete-bin-line" /> Xóa</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Tên chứng nhận *</label>
                <input className={inputCls} value={item.title} onChange={(e) => updateItem(idx, { title: e.target.value })} placeholder="ISO 9001:2015" />
              </div>
              <div>
                <label className={labelCls}>Loại</label>
                <select className={inputCls} value={item.type} onChange={(e) => updateItem(idx, { type: e.target.value as CertificationItem["type"] })}>
                  <option value="certification">Chứng nhận</option>
                  <option value="patent">Bằng sáng chế</option>
                  <option value="award">Giải thưởng</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Mô tả</label>
              <input className={inputCls} value={item.description} onChange={(e) => updateItem(idx, { description: e.target.value })} />
            </div>
            <ImageUploader images={item.image_url ? [item.image_url] : []} onChange={(urls) => updateItem(idx, { image_url: urls[0] ?? "" })} maxImages={1} folder="patents" label="Ảnh chứng nhận" />
          </div>
        ))}
      </div>
      <button type="button" onClick={addItem} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-[#004A99] hover:text-[#004A99] transition-colors cursor-pointer">
        <i className="ri-add-line" /> Thêm chứng nhận
      </button>
    </div>
  );
};

// ── CTA Editor ────────────────────────────────────────────────────────────────
const CTAEditor: React.FC<{ data: CTABlockData; onChange: (d: CTABlockData) => void }> = ({ data, onChange }) => {
  const set = <K extends keyof CTABlockData>(k: K, v: CTABlockData[K]) => onChange({ ...data, [k]: v });

  const addField = () => {
    onChange({ ...data, formFields: [...data.formFields, { label: "", type: "text", required: false }] });
  };

  const updateField = (idx: number, updated: Partial<CTABlockData["formFields"][0]>) => {
    const fields = data.formFields.map((f, i) => i === idx ? { ...f, ...updated } : f);
    onChange({ ...data, formFields: fields });
  };

  const removeField = (idx: number) => {
    onChange({ ...data, formFields: data.formFields.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        <i className="ri-mail-send-line" /> CTA / Liên hệ
      </p>
      <div>
        <label className={labelCls}>Tiêu đề</label>
        <input className={inputCls} value={data.title} onChange={(e) => set("title", e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Mô tả phụ</label>
        <input className={inputCls} value={data.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Màu nền</label>
          <div className="flex gap-2 items-center">
            <input type="color" value={data.backgroundColor} onChange={(e) => set("backgroundColor", e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-gray-200" />
            <input className={`${inputCls} flex-1`} value={data.backgroundColor} onChange={(e) => set("backgroundColor", e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Màu chữ</label>
          <div className="flex gap-2 items-center">
            <input type="color" value={data.textColor} onChange={(e) => set("textColor", e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-gray-200" />
            <input className={`${inputCls} flex-1`} value={data.textColor} onChange={(e) => set("textColor", e.target.value)} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Nhãn nút</label>
          <input className={inputCls} value={data.buttonLabel} onChange={(e) => set("buttonLabel", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Màu nút</label>
          <div className="flex gap-2 items-center">
            <input type="color" value={data.buttonColor} onChange={(e) => set("buttonColor", e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-gray-200" />
            <input className={`${inputCls} flex-1`} value={data.buttonColor} onChange={(e) => set("buttonColor", e.target.value)} />
          </div>
        </div>
      </div>
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.showForm} onChange={(e) => set("showForm", e.target.checked)} className="w-4 h-4 rounded" />
          <span className="text-xs font-semibold text-gray-700">Hiển thị form liên hệ</span>
        </label>
      </div>
      {data.showForm && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600">Các trường form:</p>
          {data.formFields.map((field, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input className={`${inputCls} flex-1`} value={field.label} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="Tên trường" />
              <select className="border border-gray-200 rounded-lg px-2 py-2 text-xs outline-none cursor-pointer" value={field.type} onChange={(e) => updateField(idx, { type: e.target.value })}>
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="tel">Phone</option>
                <option value="textarea">Textarea</option>
              </select>
              <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={field.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="w-3 h-3" />
                Bắt buộc
              </label>
              <button type="button" onClick={() => removeField(idx)} className="text-red-400 hover:text-red-600 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
          ))}
          <button type="button" onClick={addField} className="text-xs text-[#004A99] hover:underline cursor-pointer flex items-center gap-1">
            <i className="ri-add-line" /> Thêm trường
          </button>
        </div>
      )}
    </div>
  );
};

// ── Specs Grid Editor ─────────────────────────────────────────────────────────
const SpecsGridEditor: React.FC<{ data: SpecsGridBlockData; onChange: (d: SpecsGridBlockData) => void }> = ({ data, onChange }) => {
  const addItem = () => {
    onChange({ ...data, items: [...data.items, { label: "", value: "" }] });
  };

  const updateItem = (idx: number, field: "label" | "value", val: string) => {
    const items = data.items.map((item, i) => i === idx ? { ...item, [field]: val } : item);
    onChange({ ...data, items });
  };

  const removeItem = (idx: number) => {
    onChange({ ...data, items: data.items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        <i className="ri-table-line" /> Thông số kỹ thuật (Grid)
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Tiêu đề section</label>
          <input className={inputCls} value={data.title} onChange={(e) => onChange({ ...data, title: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Số cột</label>
          <select className={inputCls} value={data.columns} onChange={(e) => onChange({ ...data, columns: Number(e.target.value) as 2 | 3 | 4 })}>
            <option value={2}>2 cột</option>
            <option value={3}>3 cột</option>
            <option value={4}>4 cột</option>
          </select>
        </div>
      </div>
      <div className="space-y-2">
        {data.items.map((item, idx) => (
          <div key={idx} className="flex gap-2 items-center">
            <input className={`${inputCls} flex-1`} value={item.label} onChange={(e) => updateItem(idx, "label", e.target.value)} placeholder="Tên thông số" />
            <input className={`${inputCls} flex-1`} value={item.value} onChange={(e) => updateItem(idx, "value", e.target.value)} placeholder="Giá trị" />
            <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0"><i className="ri-close-line" /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={addItem} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-[#004A99] hover:text-[#004A99] transition-colors cursor-pointer">
        <i className="ri-add-line" /> Thêm thông số
      </button>
    </div>
  );
};

// ── Gallery Editor ────────────────────────────────────────────────────────────
const GalleryEditor: React.FC<{ data: GalleryBlockData; onChange: (d: GalleryBlockData) => void }> = ({ data, onChange }) => {
  const handleImagesChange = (urls: string[]) => {
    const images = urls.map((url, i) => ({
      url,
      caption: data.images[i]?.caption ?? "",
    }));
    onChange({ ...data, images });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        <i className="ri-gallery-line" /> Thư viện hình ảnh
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Tiêu đề section</label>
          <input className={inputCls} value={data.title} onChange={(e) => onChange({ ...data, title: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Layout</label>
          <select className={inputCls} value={data.layout} onChange={(e) => onChange({ ...data, layout: e.target.value as GalleryBlockData["layout"] })}>
            <option value="2col">2 cột</option>
            <option value="3col">3 cột</option>
            <option value="masonry">Masonry</option>
            <option value="staggered">Staggered</option>
          </select>
        </div>
      </div>
      <ImageUploader
        images={data.images.map((img) => img.url)}
        onChange={handleImagesChange}
        maxImages={12}
        folder="gallery"
        label="Hình ảnh gallery (tối đa 12)"
      />
    </div>
  );
};

// ── Video Editor ──────────────────────────────────────────────────────────────
const VideoEditor: React.FC<{ data: VideoBlockData; onChange: (d: VideoBlockData) => void }> = ({ data, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof VideoBlockData>(k: K, v: VideoBlockData[K]) => onChange({ ...data, [k]: v });

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      alert("File quá lớn (tối đa 100MB)");
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop() ?? "mp4";
    const fileName = `videos/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { data: uploadData, error } = await supabase.storage
      .from("bizwell-images")
      .upload(fileName, file, { contentType: file.type, upsert: true });
    if (!error && uploadData) {
      const { data: urlData } = supabase.storage.from("bizwell-images").getPublicUrl(uploadData.path);
      if (urlData?.publicUrl) {
        set("url", urlData.publicUrl);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
        <i className="ri-video-line" /> Video
      </p>

      <div>
        <label className={labelCls}>Link video (URL)</label>
        <input
          className={inputCls}
          value={data.url}
          onChange={(e) => set("url", e.target.value)}
          placeholder="https://example.com/video.mp4"
        />
      </div>

      <div>
        <label className={labelCls}>Hoặc tải video từ máy tính</label>
        <div
          className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#004A99]/50 hover:bg-gray-50 transition-all"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/mov,video/quicktime,video/webm,video/avi,video/x-matroska"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" />
              <p className="text-xs text-[#004A99] font-semibold">Đang tải video lên...</p>
            </div>
          ) : (
            <>
              <div className="w-8 h-8 flex items-center justify-center bg-[#EDF2FB] rounded-xl mx-auto mb-2">
                <i className="ri-video-upload-line text-lg text-[#004A99]" />
              </div>
              <p className="text-xs font-semibold text-gray-700">Nhấp để chọn file video</p>
              <p className="text-[10px] text-gray-400 mt-0.5">MP4, MOV, WebM · Tối đa 100MB</p>
            </>
          )}
        </div>
      </div>

      {data.url && (
        <div className="rounded-xl overflow-hidden border border-gray-200 bg-black">
          <video
            src={data.url}
            className="w-full max-h-[240px] object-contain"
            controls
            preload="metadata"
            style={{ display: "block" }}
          />
        </div>
      )}

      <div>
        <label className={labelCls}>Chú thích / Caption</label>
        <input
          className={inputCls}
          value={data.caption}
          onChange={(e) => set("caption", e.target.value)}
          placeholder="VD: Video giới thiệu quy trình sản xuất"
        />
      </div>

      <div>
        <label className={labelCls}>Ảnh thumbnail (poster)</label>
        <ImageUploader
          images={data.poster ? [data.poster] : []}
          onChange={(urls) => set("poster", urls[0] ?? "")}
          maxImages={1}
          folder="products"
          label="Thumbnail trước khi phát"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.controls} onChange={(e) => set("controls", e.target.checked)} className="w-4 h-4 rounded" />
          <span className="text-xs font-semibold text-gray-700">Hiện controls</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.autoplay} onChange={(e) => set("autoplay", e.target.checked)} className="w-4 h-4 rounded" />
          <span className="text-xs font-semibold text-gray-700">Tự động phát</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.loop} onChange={(e) => set("loop", e.target.checked)} className="w-4 h-4 rounded" />
          <span className="text-xs font-semibold text-gray-700">Lặp lại</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={data.muted} onChange={(e) => set("muted", e.target.checked)} className="w-4 h-4 rounded" />
          <span className="text-xs font-semibold text-gray-700">Tắt tiếng mặc định</span>
        </label>
      </div>
    </div>
  );
};

// ── Main BlockEditor ──────────────────────────────────────────────────────────
const BlockEditor: React.FC<Props> = ({ block, onChange }) => {
  const handleDataChange = (newData: BuilderBlock["data"]) => {
    onChange({ ...block, data: newData });
  };

  switch (block.data.type) {
    case "hero":
      return <HeroEditor data={block.data as HeroBlockData} onChange={(d) => handleDataChange(d)} />;
    case "story":
      return <StoryEditor data={block.data as StoryBlockData} onChange={(d) => handleDataChange(d)} />;
    case "applications":
      return <ApplicationsEditor data={block.data as ApplicationsBlockData} onChange={(d) => handleDataChange(d)} />;
    case "certifications":
      return <CertificationsEditor data={block.data as CertificationsBlockData} onChange={(d) => handleDataChange(d)} />;
    case "cta":
      return <CTAEditor data={block.data as CTABlockData} onChange={(d) => handleDataChange(d)} />;
    case "specs_grid":
      return <SpecsGridEditor data={block.data as SpecsGridBlockData} onChange={(d) => handleDataChange(d)} />;
    case "gallery":
      return <GalleryEditor data={block.data as GalleryBlockData} onChange={(d) => handleDataChange(d)} />;
    case "video":
      return <VideoEditor data={block.data as VideoBlockData} onChange={(d) => handleDataChange(d)} />;
    default:
      return <div className="text-sm text-gray-400 p-4">Không hỗ trợ loại block này</div>;
  }
};

export default BlockEditor;
